import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Trash2, Navigation } from 'lucide-react';

const PointsList = ({ points = [], onDeletePoint, coordinateSystem }) => {
  const getCoordinateLabel = () => {
    switch (coordinateSystem) {
      case 'geographic': return 'Lon, Lat';
      case 'utm29': return 'X UTM 29N, Y UTM 29N';
      case 'utm30': return 'X UTM 30N, Y UTM 30N';
      case 'utm31': return 'X UTM 31N, Y UTM 31N';
      default: return 'X, Y';
    }
  };

  if (points.length === 0) {
    return (
      <motion.div 
        className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-3xl p-8 text-center shadow-xl"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-bold text-gray-600 mb-2">
          No hay puntos cargados
        </h3>
        <p className="text-gray-500">
          Agrega puntos para comenzar a trabajar con tu nube de puntos 3D
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div 
      className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-3xl shadow-xl overflow-hidden"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.3 }}
    >
      <div className="p-4 border-b border-gray-200/50">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <Navigation className="w-5 h-5 text-blue-500" />
            Lista de Puntos ({points.length})
          </h3>
          <div className="text-sm text-gray-600">
            {getCoordinateLabel()}
          </div>
        </div>
      </div>
      
      <div className="max-h-64 overflow-y-auto">
        <AnimatePresence mode="popLayout">
          {points.map((point, index) => (
            <motion.div
              key={point.id}
              layout
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3, delay: index * 0.02 }}
              className="flex items-center justify-between p-4 border-b border-gray-100 hover:bg-gray-50/50 transition-colors group"
            >
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <span className="font-semibold text-blue-600 text-sm">
                    {point.id}
                  </span>
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    #{index + 1}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">X: </span>
                    <span className="font-mono">{point.x.toFixed(3)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Y: </span>
                    <span className="font-mono">{point.y.toFixed(3)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Z: </span>
                    <span className="font-mono font-semibold text-green-600">
                      {point.z.toFixed(3)}
                    </span>
                  </div>
                </div>
              </div>
              
              <motion.button
                onClick={() => onDeletePoint(point.id)}
                className="p-2 text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Trash2 className="w-4 h-4" />
              </motion.button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default PointsList;